import React, { Component } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    Alert,
    Chip,
    Divider,
    LinearProgress,
    CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { isAuthenticated } from "../auth";
import * as api from "./apiPoll";

class Voting extends Component {
    constructor(props) {
        super(props);
        const auth = isAuthenticated();

        this.state = {
            userId: auth && auth.userId,
            userEmail: auth && auth.email,
            userName: auth && auth.name,
            userRole: auth && auth.role,
            // Admin form
            newCandidateName: '',
            // Poll data from API
            poll: null,
            loading: true,
            // UI
            selectedCandidate: '',
            successMessage: '',
            errorMessage: '',
            saving: false,
        };
    }

    componentDidMount() {
        this.loadPoll();
    }

    loadPoll = () => {
        api.getPoll(this.props.groupId).then(data => {
            this.setState({ poll: data, loading: false });
        });
    };

    isAdmin = () => {
        const { userId, userRole } = this.state;
        const { chairId, cochairId } = this.props;
        return userId === chairId || userId === cochairId || userRole === 'admin';
    };

    hasVoted = () => {
        const { poll, userId } = this.state;
        if (!poll || !poll.votes) return false;
        return poll.votes.some(v => {
            const voterId = v.voter?._id || v.voter;
            return voterId === userId;
        });
    };

    getMyVote = () => {
        const { poll, userId } = this.state;
        if (!poll || !poll.votes) return null;
        const vote = poll.votes.find(v => {
            const voterId = v.voter?._id || v.voter;
            return voterId === userId;
        });
        return vote?.candidate;
    };

    // Admin: add a candidate name
    handleAddCandidate = () => {
        const name = this.state.newCandidateName.trim();
        if (!name) return;

        this.setState({ saving: true, errorMessage: '' });
        api.addCandidate(this.props.groupId, name).then(data => {
            this.setState({ saving: false });
            if (data.error) {
                this.setState({ errorMessage: data.error });
            } else {
                this.setState({ poll: data, newCandidateName: '' });
            }
        });
    };

    // Admin: remove a candidate
    handleRemoveCandidate = (name) => {
        api.removeCandidate(this.props.groupId, name).then(data => {
            if (data && !data.error) {
                this.setState({ poll: data });
            }
        });
    };

    // Admin: open/close the poll
    handleTogglePoll = () => {
        const newOpen = !(this.state.poll && this.state.poll.open);

        // If no poll exists yet, create one first
        if (!this.state.poll) {
            api.createOrUpdatePoll(this.props.groupId, { title: '', candidates: [] }).then(() => {
                api.togglePoll(this.props.groupId, newOpen).then(data => {
                    if (data && !data.error) this.setState({ poll: data, successMessage: '' });
                });
            });
            return;
        }

        api.togglePoll(this.props.groupId, newOpen).then(data => {
            if (data && !data.error) this.setState({ poll: data, successMessage: '' });
        });
    };

    // Admin: set poll title
    handleSetTitle = (e) => {
        const title = e.target.value;
        // Update local state immediately for responsive UI
        this.setState(prev => ({
            poll: { ...prev.poll, title }
        }));

        // Debounce the API call
        clearTimeout(this._titleTimeout);
        this._titleTimeout = setTimeout(() => {
            api.createOrUpdatePoll(this.props.groupId, { title });
        }, 500);
    };

    // Admin: reset everything
    handleResetPoll = () => {
        api.resetPoll(this.props.groupId).then(data => {
            if (data && !data.error) {
                this.setState({ poll: null, selectedCandidate: '', successMessage: '' });
            }
        });
    };

    // Member: cast vote
    handleVote = () => {
        if (!this.state.selectedCandidate) {
            this.setState({ errorMessage: 'Please select a candidate.' });
            return;
        }

        this.setState({ saving: true, errorMessage: '' });
        api.castVote(this.props.groupId, this.state.selectedCandidate, this.state.userName).then(data => {
            this.setState({ saving: false });
            if (data.error) {
                this.setState({ errorMessage: data.error });
            } else {
                this.setState({
                    poll: data,
                    successMessage: `Your vote for "${this.state.selectedCandidate}" has been recorded!`,
                });
            }
        });
    };

    // Tally votes per candidate
    getTally = () => {
        const { poll } = this.state;
        if (!poll) return {};
        const tally = {};
        (poll.candidates || []).forEach(c => { tally[c] = 0; });
        (poll.votes || []).forEach(v => {
            if (tally[v.candidate] !== undefined) {
                tally[v.candidate]++;
            }
        });
        return tally;
    };

    render() {
        const {
            poll, loading, saving,
            newCandidateName, selectedCandidate,
            successMessage, errorMessage,
        } = this.state;

        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress sx={{ color: '#003E7E' }} />
                </Box>
            );
        }

        const isAdmin = this.isAdmin();
        const hasVoted = this.hasVoted();
        const candidates = poll?.candidates || [];
        const votes = poll?.votes || [];
        const pollOpen = poll?.open || false;
        const pollTitle = poll?.title || '';
        const tally = this.getTally();
        const totalVotes = votes.length;

        return (
            <Box>
                {/* Admin Panel */}
                {isAdmin && (
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 4, backgroundColor: 'rgba(0, 62, 126, 0.02)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                            <HowToVoteIcon sx={{ fontSize: 28, color: '#FF6600' }} />
                            <Typography variant="h5" sx={{ fontWeight: 600, color: '#003E7E' }}>
                                Manage Poll
                            </Typography>
                            <Chip
                                label={pollOpen ? 'Open' : 'Closed'}
                                size="small"
                                icon={pollOpen ? <LockOpenIcon /> : <LockIcon />}
                                sx={{
                                    ml: 'auto',
                                    backgroundColor: pollOpen ? 'rgba(76, 175, 80, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                                    color: pollOpen ? '#388e3c' : '#d32f2f',
                                    fontWeight: 600,
                                }}
                            />
                        </Box>

                        {/* Poll Title */}
                        <TextField
                            label="Poll Title"
                            value={pollTitle}
                            onChange={this.handleSetTitle}
                            fullWidth
                            placeholder="e.g. 2025 Volunteer of the Year"
                            sx={{
                                mb: 3,
                                '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#003E7E' },
                                '& .MuiInputLabel-root.Mui-focused': { color: '#003E7E' },
                            }}
                        />

                        {/* Add Candidate */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                            <TextField
                                label="Candidate Name"
                                value={newCandidateName}
                                onChange={(e) => this.setState({ newCandidateName: e.target.value, errorMessage: '' })}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); this.handleAddCandidate(); } }}
                                fullWidth
                                placeholder="e.g. Jane Smith"
                                sx={{
                                    '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#003E7E' },
                                    '& .MuiInputLabel-root.Mui-focused': { color: '#003E7E' },
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={this.handleAddCandidate}
                                disabled={saving}
                                startIcon={<AddIcon />}
                                sx={{
                                    minWidth: { xs: '100%', sm: 120 },
                                    backgroundColor: '#003E7E',
                                    '&:hover': { backgroundColor: '#002d5c' }
                                }}
                            >
                                Add
                            </Button>
                        </Box>

                        {errorMessage && isAdmin && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

                        {/* Candidate List */}
                        {candidates.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: '#666' }}>
                                    Candidates ({candidates.length})
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {candidates.map((name) => (
                                        <Chip
                                            key={name}
                                            label={name}
                                            onDelete={!pollOpen ? () => this.handleRemoveCandidate(name) : undefined}
                                            deleteIcon={<DeleteIcon />}
                                            sx={{
                                                backgroundColor: '#003E7E',
                                                color: 'white',
                                                fontWeight: 600,
                                                '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } },
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Admin Actions */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="contained"
                                onClick={this.handleTogglePoll}
                                disabled={candidates.length === 0}
                                startIcon={pollOpen ? <LockIcon /> : <LockOpenIcon />}
                                sx={{
                                    backgroundColor: pollOpen ? '#d32f2f' : '#4caf50',
                                    '&:hover': { backgroundColor: pollOpen ? '#b71c1c' : '#388e3c' }
                                }}
                            >
                                {pollOpen ? 'Close Poll' : 'Open Poll'}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={this.handleResetPoll}
                                startIcon={<RestartAltIcon />}
                                sx={{
                                    borderColor: '#d32f2f',
                                    color: '#d32f2f',
                                    '&:hover': { borderColor: '#b71c1c', backgroundColor: 'rgba(211, 47, 47, 0.05)' }
                                }}
                            >
                                Reset All
                            </Button>
                        </Box>

                        {/* Results Table (admin always sees) */}
                        {votes.length > 0 && (
                            <Box sx={{ mt: 4 }}>
                                <Divider sx={{ mb: 3 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#003E7E', mb: 2 }}>
                                    Results ({totalVotes} vote{totalVotes !== 1 ? 's' : ''})
                                </Typography>
                                <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
                                    <Table size="small" sx={{ minWidth: 400 }}>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#003E7E' }}>
                                                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Candidate</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Votes</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Share</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {candidates
                                                .map(name => ({ name, count: tally[name] || 0 }))
                                                .sort((a, b) => b.count - a.count)
                                                .map(({ name, count }) => {
                                                    const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
                                                    return (
                                                        <TableRow key={name} hover>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                    {name}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Chip
                                                                    label={count}
                                                                    size="small"
                                                                    sx={{
                                                                        fontWeight: 700,
                                                                        backgroundColor: count > 0 ? '#003E7E' : '#eee',
                                                                        color: count > 0 ? 'white' : '#666',
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <LinearProgress
                                                                        variant="determinate"
                                                                        value={pct}
                                                                        sx={{
                                                                            flex: 1,
                                                                            height: 8,
                                                                            borderRadius: 4,
                                                                            backgroundColor: '#e0e0e0',
                                                                            '& .MuiLinearProgress-bar': { backgroundColor: '#FF6600', borderRadius: 4 }
                                                                        }}
                                                                    />
                                                                    <Typography variant="caption" sx={{ minWidth: 35, fontWeight: 600 }}>
                                                                        {pct}%
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Individual votes log */}
                                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: '#666' }}>
                                    Vote Log
                                </Typography>
                                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', overflowX: 'auto' }}>
                                    <Table size="small" sx={{ minWidth: 400 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Voter</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Voted For</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {votes.map((v, i) => (
                                                <TableRow key={i}>
                                                    <TableCell>{v.voterName}</TableCell>
                                                    <TableCell>{v.candidate}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {new Date(v.createdAt || v.timestamp).toLocaleString()}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}
                    </Paper>
                )}

                {/* Voter View */}
                {pollOpen && (
                    <Paper elevation={2} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                            <HowToVoteIcon sx={{ fontSize: 32, color: '#003E7E' }} />
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 600, color: '#003E7E' }}>
                                    {pollTitle || 'Committee Vote'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Select one candidate and submit your vote
                                </Typography>
                            </Box>
                        </Box>

                        {errorMessage && !isAdmin && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
                        {successMessage && <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>{successMessage}</Alert>}

                        {hasVoted ? (
                            <Alert severity="info" icon={<CheckCircleIcon />} sx={{ fontSize: '1rem' }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    Thank you for voting!
                                </Typography>
                                <Typography variant="body2">
                                    Your vote has been recorded. You voted for "{this.getMyVote()}".
                                </Typography>
                            </Alert>
                        ) : (
                            <>
                                <FormControl component="fieldset" sx={{ width: '100%' }}>
                                    <RadioGroup
                                        value={selectedCandidate}
                                        onChange={(e) => this.setState({ selectedCandidate: e.target.value, errorMessage: '' })}
                                    >
                                        {candidates.map((name) => (
                                            <Paper
                                                key={name}
                                                elevation={selectedCandidate === name ? 3 : 0}
                                                sx={{
                                                    mb: 1.5,
                                                    border: selectedCandidate === name ? '2px solid #FF6600' : '1px solid #e0e0e0',
                                                    borderRadius: 2,
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        borderColor: '#FF6600',
                                                        backgroundColor: 'rgba(255, 102, 0, 0.03)',
                                                    }
                                                }}
                                            >
                                                <FormControlLabel
                                                    value={name}
                                                    control={
                                                        <Radio sx={{ '&.Mui-checked': { color: '#FF6600' } }} />
                                                    }
                                                    label={
                                                        <Typography variant="body1" sx={{ fontWeight: 500, py: 1 }}>
                                                            {name}
                                                        </Typography>
                                                    }
                                                    sx={{ m: 0, px: 2, width: '100%' }}
                                                />
                                            </Paper>
                                        ))}
                                    </RadioGroup>
                                </FormControl>

                                <Button
                                    variant="contained"
                                    onClick={this.handleVote}
                                    disabled={!selectedCandidate || saving}
                                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <HowToVoteIcon />}
                                    size="large"
                                    fullWidth
                                    sx={{
                                        mt: 2,
                                        py: 1.5,
                                        fontWeight: 700,
                                        fontSize: '1rem',
                                        backgroundColor: '#FF6600',
                                        '&:hover': { backgroundColor: '#e55a00' }
                                    }}
                                >
                                    {saving ? 'Submitting...' : 'Submit Vote'}
                                </Button>
                            </>
                        )}
                    </Paper>
                )}

                {/* Poll closed message for non-admins */}
                {!pollOpen && !isAdmin && (
                    <Paper elevation={1} sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
                        <LockIcon sx={{ fontSize: 48, color: '#999', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                            No Active Poll
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            The committee chair will open voting when it's time.
                        </Typography>
                    </Paper>
                )}
            </Box>
        );
    }
}

export default Voting;
